<template>
  <div class="summary-block-container"
  :style="{
    minHeight: isLoading ? '100vh' : 'fit-content'
  }"
  >
    <div v-if="isLoading && !summaryContent" class="text-center py-4">
      <v-skeleton-loader
      v-for="n in 5"
      :key="`skel-${n}`"
      type="list-item-two-line"
      class=""></v-skeleton-loader>
    </div>
    <div v-else-if="error" class="text-center py-4">
      <v-alert type="error" dense outlined>
        {{ error }}
      </v-alert>
      <!-- Podríem afegir un botó per reintentar la càrrega específica del bloc si fos necessari -->
    </div>
    <div v-else-if="!summaryContent || summaryContent.trim() === ''" class="text-center py-4">
      <v-alert type="info" dense outlined>
        No hay resumen disponible para este bloque.
      </v-alert>
      <!-- Podríem afegir un botó per generar el resum només per a aquest bloc -->
    </div>
    <div v-else class="summary-content-block">
       <!-- Botón de impresión -->
       <div
       v-if="false"
       class="d-flex justify-end mb-4">
         <v-btn @click="printBlockSummary" color="#E8E8E8" text-color="#6E8071" class="print-btn rounded-xl" height="32px" x-small depressed>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6E8071" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-printer-icon lucide-printer mr-1">
               <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
               <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6" />
               <rect x="6" y="14" width="12" height="8" rx="1" />
            </svg>
            Imprimir
         </v-btn>
       </div>
      <markdown-it-vue :id="markdownId" :content="summaryContent.replace(/\\n/g, '\n')" :options="markdownOptions" 
      :class="$vuetify.breakpoint.xs ? 'pa-2' : ''"
      class="markdown-summary-block" />
    </div>
  </div>
</template>

<script>
import MarkdownItVue from 'markdown-it-vue';
import 'markdown-it-vue/dist/markdown-it-vue.css';
import 'katex/dist/katex.min.css';

export default {
  name: 'SummaryViewBlock',
  components: {
    MarkdownItVue
  },
  props: {
    documentId: {
        type: [String, Number],
        required: true
    },
    blockId: { // ID numèric del bloc a la BD (potser no el necessitem si passem contingut)
        type: [String, Number],
        default: null
    },
    blockUniqueId: { // ID únic de text del bloc
        type: String,
        required: true
    },
    summaryContent: { // Contingut del resum passat des del pare
        type: String,
        default: ''
    },
    blockName: { // Nom del bloc (per imprimir)
        type: String,
        default: 'Bloque'
    },
    isLoading: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      // Ja no necessitem isLoading/error aquí si el pare gestiona la càrrega inicial
      error: null,
      markdownOptions: {
        markdownIt: {
          linkify: false,
          html: true,
          typographer: true,
          breaks: true,
        },
        plugins: [
          ['markdown-it-katex', { 
            throwOnError: false,
            errorColor: '#cc0000',
            macros: {},
            inlineRenderer: null,
            blockRenderer: null,
            trust: true,
            strict: false
          }],
          'markdown-it-attrs',
          'markdown-it-sub',
          'markdown-it-sup',
          ['markdown-it-multimd-table', {
            multiline: true,
            rowspan: true,
            headerless: true
          }]
        ]
      }
    };
  },
  computed: {
      markdownId() {
          // ID únic per al contenidor de markdown per a la impressió
          return `markdown-summary-${this.blockUniqueId}`;
      }
  },
  methods: {
    // Si implementéssim càrrega sota demanda:
    /*
    async fetchBlockSummary() {
      this.isLoading = true;
      this.error = null;
      try {
        // --- CALDRÀ CREAR AQUEST ENDPOINT AL BACKEND --- 
        const response = await this.axios.get(`/blocks/${this.blockId}/summary`, {
          // o potser /documents/${this.documentId}/blocks/${this.blockUniqueId}/summary
          headers: { Authorization: this.$root.token.value }
        });
        if (response.data.success) {
          this.summary = response.data.summaryContent;
        } else {
          this.error = response.data.error || 'No se pudo cargar el resumen del bloque';
        }
      } catch (err) {
        console.error('Error al cargar el resumen del bloque:', err);
        this.error = 'Error al cargar el resumen del bloque.';
      } finally {
        this.isLoading = false;
      }
    },
    */

    printBlockSummary() {
      if (!this.summaryContent) return;

      const renderedContentElement = document.getElementById(this.markdownId);
      if (!renderedContentElement) {
          console.error("No se encontró el contenido renderizado para imprimir.");
          return;
      }
      const renderedContent = renderedContentElement.querySelector('.markdown-body').innerHTML;

      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      document.body.appendChild(printFrame);

      printFrame.contentDocument.write(`
        <html>
          <head>
            <title>Resumen: ${this.blockName}</title>
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.0/dist/katex.min.css">
            <style>
               body { 
                 font-family: Arial, sans-serif; 
                 line-height: 1.6; 
                 padding: 2cm; 
                 max-width: 800px;
                 margin: 0 auto;
               }
               h1 { 
                 text-align: center; 
                 font-size: 1.5rem;
                 margin-bottom: 2rem;
                 color: #333;
               }
               .content {
                 font-size: 1rem;
               }
               .content img { max-width: 100%; }
               .content p { margin-bottom: 1rem; }
               .content h2 { margin-top: 1.5rem; }
            </style>
          </head>
          <body>
            <h1>${this.blockName}</h1>
            <div class="content">${renderedContent}</div>
          </body>
        </html>
      `);

      printFrame.contentDocument.close();

      setTimeout(() => {
        printFrame.contentWindow.print();
        setTimeout(() => { document.body.removeChild(printFrame); }, 500);
      }, 1000);
    }
  },
  // Descomentar si implementem càrrega sota demanda
  /*
  watch: {
    blockId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
           this.fetchBlockSummary(); // Cridar quan el blockId estigui disponible
        }
      }
    }
  }
  */
};
</script>

<style scoped>
.summary-block-container {
  padding: 0;
  width: 100%;
}

.summary-content-block {
  line-height: 1.6;
  font-size: 1rem;
  color: #333;
  width: 100%;
}

.markdown-summary-block {
  width: 100%;
}

.markdown-summary-block >>> .markdown-body {
  font-family: 'Roboto', 'Segoe UI', Helvetica, Arial, sans-serif;
  line-height: 1.6;
  color: #333;
}

.markdown-summary-block >>> h1,
.markdown-summary-block >>> h2,
.markdown-summary-block >>> h3,
.markdown-summary-block >>> h4 {
  margin-top: 1.5em;
  margin-bottom: 0.75em;
  font-weight: 600;
}

.markdown-summary-block >>> p {
  margin-bottom: 1.2em;
}

.markdown-summary-block >>> ul,
.markdown-summary-block >>> ol {
  padding-left: 2em;
  margin-bottom: 1.2em;
}

.markdown-summary-block >>> li {
  margin-bottom: 0.5em;
}

.markdown-summary-block >>> .anchor {
  display: none !important;
}

/* Estils per a KaTeX si calen */
.markdown-summary-block >>> .katex {
  font-size: 1em; /* Ajustar mida de la font per a fórmules */
}

.markdown-summary-block >>> table {
  border-collapse: collapse;
  margin: 1em 0;
  width: 100%;
}

.markdown-summary-block >>> table td,
.markdown-summary-block >>> table th {
  border: 1px solid #ddd;
  padding: 8px;
}

.markdown-summary-block >>> table td .katex,
.markdown-summary-block >>> table th .katex {
  display: inline-block;
  white-space: normal;
}

.print-btn {
   text-transform: none !important;
   letter-spacing: 0 !important;
   font-weight: 500 !important;
   font-size: 0.8rem !important;
}
</style> 